import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Container,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as ViewIcon,
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  CalendarToday as CalendarIcon,
  Science as ScienceIcon,
  Notes as NotesIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { getCancerDiagnoses, getAllCancerDiagnoses } from '../../services/oncologyService';
import { PageHeader, HeaderButton, PageContainer, ContentCard } from './oncologyStyles';

const statusColors = {
  'Active': 'error',
  'In Remission': 'success',
  'Recurrence': 'warning',
  'Resolved': 'info',
  'Deceased': 'default'
};

const CancerDiagnosisHistory = ({ patientId: propPatientId, onEdit, onView }) => {
  const { patientId: urlPatientId } = useParams();
  const navigate = useNavigate();
  const { defaultFacility } = useUserData();
  
  const patientId = propPatientId || urlPatientId;
  const isAllDiagnoses = !patientId; // Show all diagnoses when no patient specified
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const loadDiagnoses = useCallback(async () => {
    if (!defaultFacility?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (patientId) {
        // Fetch diagnoses for specific patient
        const result = await getCancerDiagnoses(patientId, defaultFacility.id);
        setDiagnoses(result.data || []);
      } else {
        // Fetch all diagnoses for facility (facility-wide view)
        const result = await getAllCancerDiagnoses(defaultFacility.id, { limit: 50 });
        setDiagnoses(result.data || []);
      }
    } catch (err) {
      console.error('Error loading cancer diagnoses:', err);
      setError('Failed to load cancer diagnosis history');
    } finally {
      setLoading(false);
    }
  }, [defaultFacility?.id, patientId]);

  useEffect(() => {
    loadDiagnoses();
  }, [loadDiagnoses]);

  const handleView = (diagnosis) => {
    setSelectedDiagnosis(diagnosis);
    setViewDialogOpen(true);
    if (onView) onView(diagnosis);
  };

  const handleEdit = (diagnosis) => {
    if (onEdit) {
      onEdit(diagnosis);
    } else {
      navigate(`/oncology/diagnosis/edit/${diagnosis.id}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredDiagnoses = diagnoses.filter(d => 
    !searchTerm || 
    d.cancerType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.primarySite?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.patientName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        </Container>
      </PageContainer>
    );
  }

  // All Diagnoses View (no patient selected - facility-wide)
  if (isAllDiagnoses) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          {/* Header with Back Navigation */}
          <PageHeader
            title="Cancer Diagnoses"
            subtitle={`Cancer registry and diagnosis management • ${filteredDiagnoses.length} diagnosis${filteredDiagnoses.length !== 1 ? 'es' : ''}`}
            icon={<TimelineIcon />}
            backTo="/oncology"
            actions={
              <HeaderButton
                startIcon={<AddIcon />}
                onClick={() => navigate('/oncology/diagnosis/new')}
              >
                New Diagnosis
              </HeaderButton>
            }
          />

          <ContentCard>
            <CardContent>
              <TextField
                fullWidth
                placeholder="Search by cancer type, site, or patient name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {filteredDiagnoses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No cancer diagnoses found. Use the "New Diagnosis" button to create one.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.100' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Patient</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Cancer Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Primary Site</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Stage</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Diagnosis Date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDiagnoses.map((diagnosis) => (
                        <TableRow 
                          key={diagnosis.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => handleView(diagnosis)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {diagnosis.patientName || 'Unknown Patient'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {diagnosis.cancerType}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {diagnosis.primarySite}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {diagnosis.histology}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {diagnosis.staging?.clinicalStage || diagnosis.staging?.pathologicalStage || 'Not staged'}
                            </Typography>
                            {diagnosis.staging?.tStage && (
                              <Typography variant="caption" color="text.secondary">
                                ({diagnosis.staging?.tStage}{diagnosis.staging?.nStage}{diagnosis.staging?.mStage})
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={diagnosis.status}
                              size="small"
                              color={statusColors[diagnosis.status] || 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(diagnosis.diagnosisDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View details">
                              <IconButton 
                                onClick={(e) => { e.stopPropagation(); handleView(diagnosis); }} 
                                size="small"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit diagnosis">
                              <IconButton 
                                onClick={(e) => { e.stopPropagation(); navigate(`/oncology/diagnosis/edit/${diagnosis.id}`); }} 
                                size="small"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Go to patient">
                              <IconButton 
                                onClick={(e) => { e.stopPropagation(); navigate(`/patients/${diagnosis.patientId}`); }} 
                                size="small"
                              >
                                <PersonIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </ContentCard>

          {/* View Dialog */}
          <Dialog 
            open={viewDialogOpen} 
            onClose={() => setViewDialogOpen(false)} 
            maxWidth="md" 
            fullWidth
            PaperProps={{
              sx: { borderRadius: 3, overflow: 'hidden' }
            }}
          >
            {selectedDiagnosis && (
              <>
                {/* Header with gradient */}
                <Box sx={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  p: 3
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      borderRadius: 2, 
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <HospitalIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" fontWeight="bold">
                        {selectedDiagnosis.cancerType}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {selectedDiagnosis.patientName || 'Unknown Patient'}
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedDiagnosis.status}
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-label': { px: 2 }
                      }}
                    />
                  </Box>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                  {/* Primary Information */}
                  <Grid container spacing={3}>
                    {/* Diagnosis Details Section */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <HospitalIcon color="primary" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            Diagnosis Details
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Primary Site</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedDiagnosis.primarySite || 'Not specified'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Histology</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedDiagnosis.histology || 'Not specified'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Grade</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedDiagnosis.grade || 'Not specified'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Staging Section */}
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <ScienceIcon color="secondary" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                            Staging
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">TNM Classification</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                              <Chip 
                                label={`T: ${selectedDiagnosis.staging?.tStage || 'N/A'}`} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                              />
                              <Chip 
                                label={`N: ${selectedDiagnosis.staging?.nStage || 'N/A'}`} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                              />
                              <Chip 
                                label={`M: ${selectedDiagnosis.staging?.mStage || 'N/A'}`} 
                                size="small" 
                                variant="outlined"
                                color="primary"
                              />
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Clinical Stage</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedDiagnosis.staging?.clinicalStage || 'Not staged'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Pathological Stage</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              {selectedDiagnosis.staging?.pathologicalStage || 'Not staged'}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Diagnosis Date */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CalendarIcon color="action" />
                          <Box>
                            <Typography variant="caption" color="text.secondary">Diagnosis Date</Typography>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(selectedDiagnosis.diagnosisDate)}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Biomarkers */}
                    {selectedDiagnosis.biomarkers?.length > 0 && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <ScienceIcon color="info" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="bold" color="info.main">
                              Biomarkers
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {selectedDiagnosis.biomarkers.map((bm, i) => (
                              <Chip 
                                key={i} 
                                label={`${bm.biomarker}: ${bm.result}`} 
                                size="small" 
                                color={bm.result === 'Positive' ? 'success' : bm.result === 'Negative' ? 'default' : 'info'}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Paper>
                      </Grid>
                    )}

                    {/* Notes */}
                    {selectedDiagnosis.notes && (
                      <Grid item xs={12}>
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <NotesIcon color="action" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight="bold">
                              Clinical Notes
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {selectedDiagnosis.notes}
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                  <Button onClick={() => setViewDialogOpen(false)} color="inherit">
                    Close
                  </Button>
                  <Button 
                    onClick={() => { 
                      setViewDialogOpen(false); 
                      navigate(`/patients/${selectedDiagnosis?.patientId}`); 
                    }} 
                    variant="outlined"
                    endIcon={<PersonIcon />}
                  >
                    Go to Patient
                  </Button>
                  <Button 
                    onClick={() => { 
                      setViewDialogOpen(false); 
                      navigate(`/oncology/diagnosis/edit/${selectedDiagnosis?.id}`); 
                    }} 
                    variant="contained"
                    startIcon={<EditIcon />}
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                      }
                    }}
                  >
                    Edit Diagnosis
                  </Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        </Container>
      </PageContainer>
    );
  }

  // Single Patient Diagnosis History View (embedded in patient profile)
  return (
    <ContentCard>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Cancer Diagnosis History
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/oncology/diagnosis/new/${patientId}`)}
          >
            Add Diagnosis
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {diagnoses.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No cancer diagnoses recorded for this patient.
          </Typography>
        ) : (
          <List>
            {diagnoses.map((diagnosis, index) => (
              <React.Fragment key={diagnosis.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 2 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {diagnosis.cancerType}
                        </Typography>
                        <Chip
                          label={diagnosis.status}
                          size="small"
                          color={statusColors[diagnosis.status] || 'default'}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {diagnosis.primarySite} • {diagnosis.histology}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Stage: {diagnosis.staging?.clinicalStage || diagnosis.staging?.pathologicalStage || 'Not staged'} 
                          ({diagnosis.staging?.tStage}{diagnosis.staging?.nStage}{diagnosis.staging?.mStage})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Diagnosed: {formatDate(diagnosis.diagnosisDate)}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton onClick={() => handleView(diagnosis)} size="small">
                      <ViewIcon />
                    </IconButton>
                    <IconButton onClick={() => handleEdit(diagnosis)} size="small">
                      <EditIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {/* View Dialog */}
        <Dialog 
          open={viewDialogOpen} 
          onClose={() => setViewDialogOpen(false)} 
          maxWidth="md" 
          fullWidth
          PaperProps={{
            sx: { borderRadius: 3, overflow: 'hidden' }
          }}
        >
          {selectedDiagnosis && (
            <>
              {/* Header with gradient */}
              <Box sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ 
                    bgcolor: 'rgba(255,255,255,0.2)', 
                    borderRadius: 2, 
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <HospitalIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" fontWeight="bold">
                      {selectedDiagnosis.cancerType}
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedDiagnosis.status}
                    sx={{ 
                      bgcolor: 'rgba(255,255,255,0.2)', 
                      color: 'white',
                      fontWeight: 600,
                      '& .MuiChip-label': { px: 2 }
                    }}
                  />
                </Box>
              </Box>

              <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Diagnosis Details Section */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <HospitalIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                          Diagnosis Details
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Primary Site</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedDiagnosis.primarySite || 'Not specified'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Histology</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedDiagnosis.histology || 'Not specified'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Grade</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedDiagnosis.grade || 'Not specified'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Staging Section */}
                  <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <ScienceIcon color="secondary" fontSize="small" />
                        <Typography variant="subtitle1" fontWeight="bold" color="secondary">
                          Staging
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">TNM Classification</Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip 
                              label={`T: ${selectedDiagnosis.staging?.tStage || 'N/A'}`} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                            <Chip 
                              label={`N: ${selectedDiagnosis.staging?.nStage || 'N/A'}`} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                            <Chip 
                              label={`M: ${selectedDiagnosis.staging?.mStage || 'N/A'}`} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                            />
                          </Box>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Clinical Stage</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedDiagnosis.staging?.clinicalStage || 'Not staged'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Pathological Stage</Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {selectedDiagnosis.staging?.pathologicalStage || 'Not staged'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Diagnosis Date */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CalendarIcon color="action" />
                        <Box>
                          <Typography variant="caption" color="text.secondary">Diagnosis Date</Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {formatDate(selectedDiagnosis.diagnosisDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Biomarkers */}
                  {selectedDiagnosis.biomarkers?.length > 0 && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                          <ScienceIcon color="info" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="bold" color="info.main">
                            Biomarkers
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedDiagnosis.biomarkers.map((bm, i) => (
                            <Chip 
                              key={i} 
                              label={`${bm.biomarker}: ${bm.result}`} 
                              size="small" 
                              color={bm.result === 'Positive' ? 'success' : bm.result === 'Negative' ? 'default' : 'info'}
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  )}

                  {/* Notes */}
                  {selectedDiagnosis.notes && (
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <NotesIcon color="action" fontSize="small" />
                          <Typography variant="subtitle1" fontWeight="bold">
                            Clinical Notes
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {selectedDiagnosis.notes}
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>

              <DialogActions sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                <Button onClick={() => setViewDialogOpen(false)} color="inherit">
                  Close
                </Button>
                <Button 
                  onClick={() => { setViewDialogOpen(false); handleEdit(selectedDiagnosis); }} 
                  variant="contained"
                  startIcon={<EditIcon />}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                    }
                  }}
                >
                  Edit Diagnosis
                </Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </CardContent>
    </ContentCard>
  );
};

export default CancerDiagnosisHistory;
